import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { clearPageCache } from "./ui";
import { clearSlugs } from "./slug";

const OWNER = "larus:cache-owner";
/* Cached pages and addresses belong to one signed-in person; anyone else starts clean. */
const wipe = () => { clearPageCache(); clearSlugs(); sessionStorage.removeItem(OWNER); };
const claim = (u: User) => { if (sessionStorage.getItem(OWNER) !== u.id) { wipe(); sessionStorage.setItem(OWNER, u.id); } return u; };

export type User = { id: string; email: string; name: string; entity_id: string; entity_name: string; is_platform_admin: boolean; onboarded?: boolean };
type Ctx = { user: User | null | false; login: (e: string, p: string) => Promise<void>; logout: () => Promise<void>; setUser: (u: User | false) => void };

const AuthCtx = createContext<Ctx>({} as Ctx);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null | false>(null);
  useEffect(() => {
    api<User>("/auth/me").then((u) => setUser(claim(u))).catch(() => { wipe(); setUser(false); });
    const onExpire = () => { wipe(); setUser(false); };
    window.addEventListener("larus:unauthorised", onExpire);
    return () => { window.removeEventListener("larus:unauthorised", onExpire); };
  }, []);
  const login = useCallback(async (email: string, password: string) => {
    setUser(claim(await api<User>("/auth/login", { body: { email, password } })));
  }, []);
  const logout = useCallback(async () => {
    try { await api("/auth/logout", { method: "POST" }); } finally { wipe(); setUser(false); }
  }, []);
  return <AuthCtx.Provider value={{ user, login, logout, setUser }}>{children}</AuthCtx.Provider>;
}
